import {useEffect,useMemo,useState,type FormEvent} from 'react';
import {CatalogStatusSwitch} from './CatalogStatusSwitch';
type Category='packages'|'memberships'|'gift_cards';
type Allocation={allocationNumber:number;serviceName:string;serviceId:string|null;quantity:number};
type Offer={
  category:Category;sourceRow:number|null;ownerCreated?:boolean;
  title:string;description:string;
  priceCents:number|null;firstPaymentCents:number|null;
  recurringPriceCents:number|null;expirationDays:number|null;
  repeatPeriod:string|null;mode:string|null;
  publiclyListed:boolean;enabled:boolean;version:number;
  giftCardTitle:string|null;giftLegalText:string|null;giftRecipientText:string|null;
  productId:string|null;secretLinkPath:string|null;allocations:Allocation[];
};
type GiftDeliveryRow={
  voucherId:string;buyerName:string;title:string;recipientName:string;
  destinationEmail:string;paidAt:string;originalStatus:string;redeemed:boolean;
  refunded:boolean;resendCount:number;lastResendStatus:string|null;
};
type Response={ok:boolean;data?:{sourceTag:string;offers:Offer[];canEdit:boolean};
  error?:{code:string}};
const labels:Record<Category,string>={
  packages:'Packages',memberships:'Memberships',gift_cards:'Gift Cards',
};
const money=(value:number|null)=>value===null?'Not recorded':
  new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(value/100);
const cents=(value:string)=>{const n=Number(value);
  if(!Number.isFinite(n)||n<0||!Number.isInteger(Math.round(n*100))||
    n>1000000)throw Error('Enter a valid price.');return Math.round(n*100);};
const dollars=(value:number|null)=>value===null?'':String(value/100);
const draftCopy=(offer:Offer):Offer=>({...offer,
  allocations:offer.allocations.map(a=>({...a}))});

/** Owner-requested Setup catalog editor. Preserves original Helm source rows,
 * exact service-credit allocations and stable Secret Links. Membership recurring
 * charges and gift-card paid fulfillment remain independently disabled. */
export function SourceCatalogSetupWorkspace({locationCode,category}:{
  locationCode:string;category:Category;
}){
  const [offers,setOffers]=useState<Offer[]>([]);
  const [canEdit,setCanEdit]=useState(false);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [search,setSearch]=useState('');
  const [selected,setSelected]=useState<string|null>(null);
  const [draft,setDraft]=useState<Offer|null>(null);
  const [price,setPrice]=useState('');
  const [first,setFirst]=useState('');
  const [recurring,setRecurring]=useState('');
  const [expiry,setExpiry]=useState('');
  const [neverExpires,setNeverExpires]=useState(true);
  const [saving,setSaving]=useState(false);
  const [statusBusy,setStatusBusy]=useState<string|null>(null);
  const [pickedService,setPickedService]=useState('');
  const [setupServices,setSetupServices]=useState<Array<{id:string;name:string}>>([]);
  const [creationKey,setCreationKey]=useState('');
  const [saveStatus,setSaveStatus]=useState('');
  const [sendDueBusy,setSendDueBusy]=useState(false);
  const [giftOperationsOpen,setGiftOperationsOpen]=useState(false);
  const [giftOperationsBusy,setGiftOperationsBusy]=useState(false);
  const [giftDeliveries,setGiftDeliveries]=useState<GiftDeliveryRow[]>([]);
  const [giftOperationsError,setGiftOperationsError]=useState('');
  const [resendBusy,setResendBusy]=useState<string|null>(null);
  const refresh=async(signal?:AbortSignal)=>{
    const response=await fetch('/api/v1/staff/sales/source-catalog?'+
      new URLSearchParams({location:locationCode}),
      {credentials:'same-origin',cache:'no-store',signal});
    const body=await response.json() as Response;
    if(!response.ok||!body.ok||!body.data)
      throw Error('Source catalog unavailable for this location.');
    if(signal?.aborted)return;
    setOffers(body.data.offers);
    setCanEdit(body.data.canEdit===true);
  };
  useEffect(()=>{
    const abort=new AbortController();
    setLoading(true);setError('');setOffers([]);setSelected(null);
    setDraft(null);setSaveStatus('');
    setGiftOperationsOpen(false);setGiftDeliveries([]);
    setGiftOperationsError('');
    void refresh(abort.signal).catch(()=>{
      if(!abort.signal.aborted)setError('Source catalog unavailable for this location.');
    }).finally(()=>{if(!abort.signal.aborted)setLoading(false);});
    return ()=>abort.abort();
  },[locationCode]);
  const serviceChoices=useMemo(()=>{
    const choices=new Map<string,string>();
    for(const service of setupServices)choices.set(service.id,service.name);
    for(const offer of offers)for(const a of offer.allocations){
      if(a.serviceId&&!choices.has(a.serviceId))
        choices.set(a.serviceId,a.serviceName||'Service credit');
    }
    return [...choices.entries()].map(([id,name])=>({id,name}))
      .sort((a,b)=>String(a.name).localeCompare(String(b.name)));
  },[offers,setupServices]);
  useEffect(()=>{
    const abort=new AbortController();setSetupServices([]);
    void fetch('/api/v1/staff/services?'+
      new URLSearchParams({location:locationCode}),{
        credentials:'same-origin',cache:'no-store',signal:abort.signal,
      }).then(async response=>{
        const body=await response.json() as {ok:boolean;data?:{
          services:Array<{id:string;name:string;active:boolean;
            locationActive:boolean}>;
        }};
        if(!response.ok||!body.ok||!body.data?.services)return;
        if(!abort.signal.aborted)setSetupServices(body.data.services
          .filter(item=>item.active&&item.locationActive)
          .map(item=>({id:item.id,name:item.name})));
      }).catch(()=>{/* Keep only already verified catalog allocations. */});
    return ()=>abort.abort();
  },[locationCode]);
  const shown=useMemo(()=>offers.filter(item=>item.category===category&&
    item.title.toLowerCase().includes(search.trim().toLowerCase())),
    [offers,category,search]);
  const choose=(offer:Offer)=>{
    setSelected(offer.productId);setDraft(draftCopy(offer));
    setPrice(dollars(offer.priceCents));setFirst(dollars(offer.firstPaymentCents));
    setRecurring(dollars(offer.recurringPriceCents));
    setNeverExpires(offer.expirationDays===null);
    setExpiry(offer.expirationDays===null?'':String(offer.expirationDays));
    setSaveStatus('');window.scrollTo({top:0,behavior:'instant'});
  };
  const beginNew=()=>{
    const blank:Offer={category,sourceRow:null,ownerCreated:true,title:'',
      description:'',enabled:true,publiclyListed:false,version:1,
      productId:null,secretLinkPath:null,priceCents:category==='memberships'?null:0,
      firstPaymentCents:category==='memberships'?0:null,
      recurringPriceCents:category==='memberships'?0:null,
      expirationDays:null,repeatPeriod:category==='memberships'?'per month':null,
      mode:category==='gift_cards'?'service credit':null,
      giftCardTitle:null,giftLegalText:null,giftRecipientText:null,
      allocations:[]};
    setCreationKey(crypto.randomUUID());
    setSelected('new');setDraft(blank);setPrice('');setFirst('');setRecurring('');
    setExpiry('');setNeverExpires(true);setPickedService('');setSaveStatus('');
    window.scrollTo({top:0,behavior:'instant'});
  };
  const update=(fields:Partial<Offer>)=>setDraft(d=>d?{...d,...fields}:d);
  const save=async(event:FormEvent)=>{
    event.preventDefault();
    if(!draft||saving)return;
    setSaving(true);setSaveStatus('');
    try{
      if(draft.allocations.some(a=>!a.serviceId))
        throw Error('Unmatched service allocations must be mapped before saving.');
      const expirationDays=neverExpires?null:
        expiry.trim()===''?NaN:Number(expiry);
      if(expirationDays!==null&&(!Number.isInteger(expirationDays)||
        expirationDays<0||expirationDays>36500))
        throw Error('Expiration must be a whole number of days or No expiration.');
      if(draft.allocations.length===0)
        throw Error('Add at least one included service credit.');
      const creating=draft.productId===null;
      const response=await fetch('/api/v1/staff/sales/catalog-product',{
        method:creating?'POST':'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,
          ...(creating?{repeatPeriod:draft.repeatPeriod,
            idempotencyKey:creationKey}:
            {productId:draft.productId,expectedVersion:draft.version}),
          category:draft.category,
          title:draft.title,description:draft.description,
          enabled:draft.enabled,publiclyListed:draft.publiclyListed,
          priceCents:category==='memberships'?null:cents(price),
          firstPaymentCents:category==='memberships'?cents(first):null,
          recurringPriceCents:category==='memberships'?cents(recurring):null,
          expirationDays,giftCardTitle:category==='gift_cards'?draft.giftCardTitle:null,
          giftLegalText:category==='gift_cards'?draft.giftLegalText:null,
          giftRecipientText:category==='gift_cards'?draft.giftRecipientText:null,
          allocations:draft.allocations.map(a=>({
            allocationNumber:a.allocationNumber,serviceId:a.serviceId,
            quantity:a.quantity,
          })),
        }),
      });
      const body=await response.json() as Response;
      if(response.status===409)
        throw Error('This product was changed elsewhere. Reload the catalog and retry.');
      if(!response.ok||!body.ok)
        throw Error(body.error?.code==='ACCESS_DENIED'?
          'You do not have permission to edit this Setup product.':
          'Product could not be saved. No changes were applied.');
      await refresh();
      setSelected(null);setDraft(null);
      setSaveStatus(creating?'Product created.':'Product saved.');
    }catch(e){setSaveStatus(e instanceof Error?e.message:'Product could not be saved.');}
    finally{setSaving(false);}
  };
  const setOfferEnabled=async(item:Offer)=>{
    if(!canEdit||saving||statusBusy||!item.productId)return;
    setStatusBusy(item.productId);setSaveStatus('');
    try{
      const response=await fetch('/api/v1/staff/sales/catalog-product/'+
        encodeURIComponent(item.productId)+'/status',{
        method:'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,
          enabled:!item.enabled,expectedVersion:item.version}),
      });
      const body=await response.json() as {ok:boolean;error?:{code?:string}};
      if(!response.ok||!body.ok)throw Error(body.error?.code??'Status not saved.');
      await refresh();setSelected(null);setDraft(null);
      setSaveStatus(item.title+(item.enabled?' disabled.':' enabled.'));
    }catch(e){setSaveStatus(e instanceof Error?e.message:'Status not saved.');}
    finally{setStatusBusy(null);}
  };
  const sendDue=async()=>{
    setSendDueBusy(true);setSaveStatus('');
    try{
      const response=await fetch('/api/v1/staff/gifts/test-dispatch-due',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode}),
      });
      const body=await response.json() as {ok:boolean;data?:{
        attempted:number;accepted:number;reviewRequired:number};error?:{code:string}};
      if(!response.ok||!body.ok||!body.data)
        throw Error(body.error?.code??'Unable to send due TEST gift emails.');
      setSaveStatus('TEST gift email processing: '+body.data.accepted+
        ' accepted by Zoho, '+body.data.reviewRequired+' require review, '+
        body.data.attempted+' attempted.');
    }catch(e){setSaveStatus(e instanceof Error?e.message:'Unable to send due gifts.');}
    finally{setSendDueBusy(false);}
  };
  const loadGiftDeliveries=async()=>{
    setGiftOperationsBusy(true);setGiftOperationsError('');
    try{
      const response=await fetch('/api/v1/staff/gifts/test-deliveries?'+
        new URLSearchParams({location:locationCode}),{
        credentials:'same-origin',cache:'no-store',
      });
      const body=await response.json() as {ok:boolean;data?:{
        gifts:GiftDeliveryRow[]};error?:{code:string}};
      if(!response.ok||!body.ok||!body.data?.gifts)
        throw Error(body.error?.code??'Gift Card delivery history unavailable.');
      setGiftDeliveries(body.data.gifts);
    }catch(e){
      setGiftOperationsError(e instanceof Error?e.message:
        'Gift Card delivery history unavailable.');
    }finally{setGiftOperationsBusy(false);}
  };
  const requestStaffGiftResend=async(gift:GiftDeliveryRow)=>{
    if(resendBusy)return;
    setResendBusy(gift.voucherId);setGiftOperationsError('');
    try{
      const response=await fetch('/api/v1/staff/gifts/test-resend',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,voucherId:gift.voucherId,
          requestKey:crypto.randomUUID()}),
      });
      const body=await response.json() as {ok:boolean;data?:{
        status:string;destinationEmail:string};error?:{code:string}};
      if(!response.ok||!body.ok||!body.data)
        throw Error(body.error?.code==='TEST_GIFT_RESEND_IN_PROGRESS'?
          'Another resend is queued or requires review.':
          body.error?.code??'Gift Card resend could not be queued.');
      setSaveStatus('Gift Card resend queued to '+body.data.destinationEmail+
        '. Verify provider acceptance in the delivery history.');
      await loadGiftDeliveries();
    }catch(e){
      setGiftOperationsError(e instanceof Error?e.message:
        'Gift Card resend could not be queued.');
    }finally{setResendBusy(null);}
  };
  return <section className="ecfs-source-setup" aria-label={labels[category]+' setup'}>
    <header className="ecfs-products-heading"><h1>{labels[category]}</h1>
      </header>
    {loading&&<p role="status">Loading {labels[category]}…</p>}
    {error&&<p role="alert">{error}</p>}
    {!loading&&!error&&<>



      <label>Search {labels[category]}
        <input type="search" value={search} onChange={e=>setSearch(e.target.value)}/>
      </label>
      {category==='gift_cards'&&canEdit&&<button type="button"
        disabled={sendDueBusy} onClick={()=>void sendDue()}>
        {sendDueBusy?'Checking due gifts…':'Send Due TEST Gift Emails'}</button>}
      {category==='gift_cards'&&canEdit&&<section
        aria-label="Gift Card Delivery & Resend History" className="ecfs-source-setup-editor">
        <h2>Gift Card Delivery &amp; Resend History (TEST)</h2>
        <p>Purchased test gifts at {locationCode}. Resends use the original
          delivery email and do not issue new credits. An accepted email means
          the provider accepted it for sending, not that the inbox received it.</p>
        <button type="button" onClick={()=>{
          const next=!giftOperationsOpen;
          setGiftOperationsOpen(next);
          if(next)void loadGiftDeliveries();
        }}>{giftOperationsOpen?'Hide Gift Card History':'Show Gift Card History'}</button>
        {giftOperationsOpen&&<>
          <button type="button" disabled={giftOperationsBusy}
            onClick={()=>void loadGiftDeliveries()}>
            {giftOperationsBusy?'Refreshing…':'Refresh Gift Card History'}</button>
          {giftOperationsError&&<p role="alert">{giftOperationsError}</p>}
          {!giftOperationsBusy&&giftDeliveries.length===0&&
            !giftOperationsError&&<p>No verified paid TEST Gift Cards at this location.</p>}
          {giftDeliveries.length>0&&<table className="ecfs-record-table">
            <thead><tr><th>Gift</th><th>Buyer</th><th>Recipient / Email</th>
              <th>Original Delivery</th><th>Resend History</th><th>Action</th>
            </tr></thead>
            <tbody>{giftDeliveries.map(g=>{
              const pending=['PENDING_SENDER','SENDING','REVIEW_REQUIRED']
                .includes(g.lastResendStatus??'');
              const allowed=!g.refunded&&g.originalStatus==='ACCEPTED'&&!pending;
              return <tr key={g.voucherId}>
                <td><strong>{g.title}</strong>
                  <small> Purchased {new Date(g.paidAt).toLocaleDateString()}</small>
                  {g.redeemed&&<small> Redeemed</small>}</td>
                <td>{g.buyerName}</td>
                <td>{g.recipientName||'Unspecified'}<small>
                  {' '} {g.destinationEmail||'Not configured'}</small></td>
                <td>{g.refunded?'Refunded':g.originalStatus==='ACCEPTED'?
                    'Accepted by Zoho':g.originalStatus==='REVIEW_REQUIRED'?
                    'Needs review':g.originalStatus==='PENDING_SENDER'?
                    'Pending delivery':'Sending'}</td>
                <td>{g.resendCount} requested
                  {g.lastResendStatus&&<small>
                    {' '}Latest: {g.lastResendStatus==='ACCEPTED'?'Accepted':
                      g.lastResendStatus==='REVIEW_REQUIRED'?'Needs review':
                      g.lastResendStatus==='SENDING'?'Sending':'Queued'}</small>}
                </td>
                <td><button type="button" disabled={!allowed||Boolean(resendBusy)}
                  onClick={()=>void requestStaffGiftResend(g)}>
                  {resendBusy===g.voucherId?'Queuing…':'Resend Gift Card'}</button></td>
              </tr>;
            })}</tbody>
          </table>}
        </>}
      </section>}
      {canEdit&&<button type="button" onClick={beginNew}>
        Add a {labels[category].slice(0,-1)}</button>}
      {draft&&canEdit&&selected===(draft.productId??'new')&&<form
        aria-label={'Edit '+labels[category].slice(0,-1)+' definition'}
        className="ecfs-source-setup-editor" onSubmit={e=>void save(e)}>
        <h2>{draft.productId?'Edit '+draft.title:'Add a '+labels[category].slice(0,-1)}</h2>
        <label>Name <input required maxLength={320} value={draft.title}
          onChange={e=>update({title:e.target.value})}/></label>
        <label>Description <textarea value={draft.description}
          onChange={e=>update({description:e.target.value})}/></label>
        {category==='memberships'?<>
          <label>First payment ($) <input type="number" required min={0} step="0.01"
            value={first} onChange={e=>setFirst(e.target.value)}/></label>
          <label>Recurring payment ($) <input type="number" required min={0} step="0.01"
            value={recurring} onChange={e=>setRecurring(e.target.value)}/></label>
          {draft.productId?<p>Repeat period: {draft.repeatPeriod??'Not recorded'}</p>:
            <label>Repeat period <select aria-label="Repeat period"
              value={draft.repeatPeriod??'per month'}
              onChange={e=>update({repeatPeriod:e.target.value})}>
              <option value="per month">Per month</option>
              <option value="every 12 months">Every 12 months</option>
            </select></label>}
        </>:<label>Price ($) <input type="number" required min={0} step="0.01"
          value={price} onChange={e=>setPrice(e.target.value)}/></label>}
        <label>No expiration <input type="checkbox" checked={neverExpires}
          onChange={e=>{setNeverExpires(e.target.checked);
            if(e.target.checked)setExpiry('');}}/></label>
        {!neverExpires&&<label>Expiration after purchase (days)
          <input type="number" required min={0} max={36500} step={1}
            value={expiry} onChange={e=>setExpiry(e.target.value)}/></label>}
        <label>Available to Customers in Public Store
          <input type="checkbox" checked={draft.publiclyListed}
            onChange={e=>update({publiclyListed:e.target.checked})}/></label>
        <fieldset><legend>Included service credits</legend>
          {<div>
            <label>Add included service
              <select aria-label="Add included service" value={pickedService}
                onChange={e=>setPickedService(e.target.value)}>
                <option value="">Choose service</option>
                {serviceChoices.filter(choice=>!draft.allocations.some(a=>
                  a.serviceId===choice.id)).map(choice=>
                  <option value={choice.id} key={choice.id}>{choice.name}</option>)}
              </select></label>
            <button type="button" disabled={!pickedService||draft.allocations.length>=50}
              onClick={()=>{
                const choice=serviceChoices.find(c=>c.id===pickedService);
                if(!choice)return;
                const number=Math.max(0,...draft.allocations.map(a=>a.allocationNumber))+1;
                update({allocations:[...draft.allocations,{allocationNumber:number,
                  serviceName:choice.name,serviceId:choice.id,quantity:1}]});
                setPickedService('');
              }}>Add service credit</button>
          </div>}
          {draft.allocations.map(a=><label key={a.allocationNumber}>
            {a.serviceName} <input type="number" min={1} max={365} step={1}
              aria-label={'Quantity for '+a.serviceName}
              value={a.quantity} disabled={!a.serviceId}
              onChange={e=>update({allocations:draft.allocations.map(b=>
                b.allocationNumber===a.allocationNumber?
                  {...b,quantity:Number(e.target.value)}:b)})}/>
              {<button type="button"
                onClick={()=>update({allocations:draft.allocations.filter(b=>
                  b.allocationNumber!==a.allocationNumber)})}>Remove</button>}
            </label>)}
        </fieldset>
        {category==='gift_cards'&&<>
          <label>Title Shown On Card <input value={draft.giftCardTitle??''}
            onChange={e=>update({giftCardTitle:e.target.value})}/></label>
          <label>Legal Text <textarea value={draft.giftLegalText??''}
            onChange={e=>update({giftLegalText:e.target.value})}/></label>
          <label>Recipient Text <textarea value={draft.giftRecipientText??''}
            onChange={e=>update({giftRecipientText:e.target.value})}/></label>
        </>}
        <div className="ecfs-products-editor-actions">
          <button type="submit" disabled={saving}>
            {saving?'Saving…':(draft.productId?'Save ':'Create ')+
              labels[category].slice(0,-1)}</button>
          <button type="button" disabled={saving} onClick={()=>{
            setDraft(null);setSelected(null);setSaveStatus('');
          }}>Cancel</button>
        </div>
      </form>}
      {shown.length===0?<p>No matching {labels[category]}.</p>:
        <table className="ecfs-record-table">
          <thead><tr><th>Status</th><th>Name</th><th>Price</th>
            <th>Contents</th><th>Expiration</th><th>Secret Link</th>
            {canEdit&&<th>Setup</th>}</tr></thead>
          <tbody>{shown.map(item=><tr key={item.productId??item.sourceRow}>
            <td><CatalogStatusSwitch enabled={item.enabled}
              label={item.title+' enabled'}
              busy={statusBusy===item.productId}
              disabled={!canEdit||saving||!item.productId}
              onToggle={()=>void setOfferEnabled(item)}/>
              {item.publiclyListed&&<small> Public Store</small>}</td>
            <td>{item.title}{item.ownerCreated&&<small> Owner-created</small>}</td>
            <td>{money(item.priceCents)}
              {item.recurringPriceCents!==null&&
                <small> Recurring: {money(item.recurringPriceCents)}</small>}
              {item.firstPaymentCents!==null&&
                <small> First: {money(item.firstPaymentCents)}</small>}</td>
            <td>{item.allocations.map(a=>a.quantity+' × '+a.serviceName).join(', ')||
              item.repeatPeriod||'No linked service allocation'}</td>
            <td>{item.expirationDays===null?'No expiration':
              item.expirationDays+' days'}</td>
            <td>{item.secretLinkPath?
              <><a href={item.secretLinkPath} target="_blank" rel="noopener noreferrer">
                Existing Secret Link</a>
                {category!=='packages'&&<small> Configured, payments not enabled</small>}</>
              :'Not generated'}</td>
            {canEdit&&<td><button type="button"
              aria-label={'Edit '+item.title} disabled={!item.productId}
              onClick={()=>choose(item)}>Edit</button></td>}
          </tr>)}</tbody>
        </table>}
      {saveStatus&&<p role={saveStatus.startsWith('Product ')?'status':'alert'}>
        {saveStatus}</p>}

    </>}
  </section>;
}
